import { useState, useEffect, useRef } from "react";

import { Table, tableFromArrays } from "apache-arrow";
import {
  extendTheme,
  ChakraProvider,
  Box,
  Container,
  Flex,
  Heading,
} from "@chakra-ui/react";
import Logo from "./components/Logo";

import "./App.css";
import StatTable from "./components/StatTable";
import WeekFilters from "./components/WeekFilters";
import YearFilters from "./components/YearFilters";
import RankHistoryChart from "./components/RankHistoryChart";
import { loadSeasonData, SeasonData } from "./loadSeasonData";

import data2024 from "./data/2024.json";
import data2025 from "./data/2025.json";
import data2026 from "./data/2026.json";

const theme = extendTheme({
  config: {
    useSystemColorMode: false,
  },
  styles: {
    global: {
      body: {
        bg: "#f8f5f0",
        color: "#2d3748",
      },
    },
  },
});

const statFormatter = ({
  value,
  column,
}: {
  value: string | number;
  column: { id: string };
}) => {
  if (column.id === "avg" || column.id === "obp" || column.id === "slg") {
    return (+value).toFixed(3).replace("0.", ".");
  } else {
    return String(value);
  }
};

const rankFormatter = ({
  value,
  column,
}: {
  value: string | number;
  column: { id: string };
}) => {
  return String(value);
};

const seasonFiles: { [key: string]: SeasonData } = {
  "2024": data2024 as SeasonData,
  "2025": data2025 as SeasonData,
  "2026": data2026 as SeasonData,
};

const history: { [key: string]: Table[] } = {};
for (const [year, data] of Object.entries(seasonFiles)) {
  const tables = loadSeasonData(data);
  if (tables.length > 0) {
    history[year] = tables;
  }
}

const computeRankings = (history: Table[], yearKey: number) => {
  const numTeams = history[0].numRows;
  const teams = history[0].getChild("team")!;
  const teamNames: string[] = [];
  for (let i = 0; i < numTeams; i++) {
    teamNames.push(teams.get(i));
  }

  const rankDesc = (arr: number[]) => {
    const sorted = [...arr].sort((a, b) => b - a);
    return arr.map((x: number) => numTeams - sorted.indexOf(x));
  };

  return history.map((tbl) => {
    const abs = rankDesc(tbl.getChild("ab")!.toArray());
    const runs = rankDesc(tbl.getChild("run")!.toArray());
    const hits = rankDesc(tbl.getChild("hit")!.toArray());
    const hr = rankDesc(tbl.getChild("hr")!.toArray());
    const rbi = rankDesc(tbl.getChild("rbi")!.toArray());
    const sb = rankDesc(tbl.getChild("sb")!.toArray());
    const bb = rankDesc(tbl.getChild("bb")!.toArray());
    const avg = rankDesc(tbl.getChild("avg")!.toArray());
    const obp = rankDesc(tbl.getChild("obp")!.toArray());
    const slg = rankDesc(tbl.getChild("slg")!.toArray());

    const totals = [];
    for (let i = 0; i < tbl.numRows; i++) {
      const total =
        abs[i] +
        runs[i] +
        hits[i] +
        hr[i] +
        rbi[i] +
        sb[i] +
        bb[i] +
        avg[i] +
        obp[i] +
        slg[i];
      totals.push(total);
    }
    return tableFromArrays({
      team: teamNames,
      ab: abs,
      run: runs,
      hit: hits,
      hr: hr,
      rbi: rbi,
      sb: sb,
      bb: bb,
      avg: avg,
      slg: slg,
      obp: obp,
      total: totals,
    });
  });
};

function App() {
  const years = Object.keys(history).map(Number).sort((a, b) => b - a);
  const defaultYear = years[0];
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const yearValues = history[String(selectedYear)];
  const defaultWeek = yearValues.length;
  const [selectedWeek, setSelectedWeek] = useState(defaultWeek);

  const tableFlexRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (tableFlexRef.current) {
        setTableWidth(tableFlexRef.current.scrollWidth);
      }
    };
    // Delay first measurement to ensure tables have rendered
    const frame = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
    };
  }, [selectedYear, selectedWeek]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedWeek(1);
  };

  const data = yearValues[selectedWeek - 1];
  const rankHistory = computeRankings(yearValues, selectedYear);
  const selectedRankings = rankHistory[selectedWeek - 1];

  return (
    <ChakraProvider theme={theme}>
      <Box bg="#1a2744" py={4} px={6} mb={8} boxShadow="0 2px 8px rgba(0,0,0,0.15)">
        <Flex align="center" maxW="container.xl" mx="auto">
          <Logo size={56} />
          <Box ml={4}>
            <Heading size="xl" color="white" letterSpacing="wide" fontWeight="extrabold">
              SoCal Math Machine
            </Heading>
          </Box>
        </Flex>
      </Box>

      <Container maxWidth="container.xl" pb={8}>
        <Box mb={5}>
          <YearFilters
            years={Object.keys(history).map(Number)}
            setYear={handleYearChange}
            selectedYear={selectedYear}
          />
        </Box>

        <Box mb={6}>
          <RankHistoryChart rankHistory={rankHistory} width={tableWidth} />
        </Box>

        <Box mb={5}>
          <WeekFilters
            weeks={Array.from({ length: defaultWeek }, (v, i) => i + 1)}
            setWeek={setSelectedWeek}
            selectedWeek={selectedWeek}
          />
        </Box>

        <Flex ref={tableFlexRef} direction={{ base: "column", md: "row" }}>
          <Box
            flex={1}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            p={5}
            border="1px solid"
            borderColor="gray.100"
            mr={{ base: 0, md: 3 }}
            mb={{ base: 4, md: 0 }}
          >
            <StatTable
              data={data}
              header="Total Stats"
              formatter={statFormatter}
            />
          </Box>
          <Box
            flex={1}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            p={5}
            border="1px solid"
            borderColor="gray.100"
            ml={{ base: 0, md: 3 }}
          >
            <StatTable
              data={selectedRankings}
              header="Rankings"
              formatter={rankFormatter}
            />
          </Box>
        </Flex>
      </Container>
    </ChakraProvider>
  );
}

export default App;
